import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TokenStorageService } from './token-storage.service';

export interface LoginRequest { email: string; password: string; }
export interface LoginResponse { access_token: string; refresh_token?: string; }
export interface UserProfile {
  id: string | number;
  email: string;
  name?: string;
  roles?: string[];
  isActive?: boolean;
  dateOfBirth?: string;
  lastLoginAt?: string | Date;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokens = inject(TokenStorageService);

  private readonly _user = signal<UserProfile | null>(null);
  readonly user = computed(() => this._user());
  readonly isAuthenticated = computed(() => this.tokens.has());

  /** Papéis permitidos no admin */
  private readonly allowedRoles = ['ADMIN', 'DEVELOPER', 'SUPPORT', 'SUPERUSER'] as const;

  // ========= Fluxo de sessão =========

  /** Chamado no login e no APP_INITIALIZER */
  async fetchMe(): Promise<void> {
    const url = environment.apiBaseUrl + environment.endpoints.me;
    const me = await firstValueFrom(this.http.get<UserProfile>(url));
    this._user.set(me);
  }

  async login(payload: LoginRequest): Promise<void> {
    const url = environment.apiBaseUrl + environment.endpoints.login;
    const res = await firstValueFrom(this.http.post<LoginResponse>(url, payload));
    this.tokens.set(res.access_token);                  // centraliza no TokenStorageService
    await this.fetchMe();                               // interceptor já anexa o token
  }

  /** Restaura sessão ao abrir/recarregar a app (se houver token salvo). */
  async tryRestoreSession(): Promise<void> {
    if (!this.tokens.has()) {
      this._user.set(null);
      return;
    }
    try {
      await this.fetchMe();
    } catch {
      // token inválido/expirado
      this.logout();
    }
  }

  logout(): void {
    this.tokens.clear();
    this._user.set(null);
  }

  hasAllowedRole(): boolean {
    const u = this._user();
    if (!u?.roles?.length) return false;
    return u.roles.some(r => (this.allowedRoles as readonly string[]).includes(r));
  }

  // ========= Esqueci/Reset =========

  requestPasswordReset(email: string): Promise<unknown> {
    const url = environment.apiBaseUrl + environment.endpoints.forgot;
    return firstValueFrom(this.http.post(url, { email }));
  }

  resetPassword(token: string, newPassword: string): Promise<unknown> {
    const url = environment.apiBaseUrl + environment.endpoints.reset;
    return firstValueFrom(this.http.post(url, { token, newPassword }));
  }
}
